import { StateService } from '@uirouter/core';
import angular, { IScope } from 'angular';
import { ngStorage } from 'ngstorage';
import {
    FontStyleService,
    FoundryService,
    hasOwnProperty,
    IProjectSummary,
    LICENSE_TYPE_DEFS,
    OrderService,
    replaceCollection,
} from '../../tn.common';
import { GtmService } from '../../tn.common/classes/gtm/GtmService';
import { LicenseListService } from '../../tn.licenses/license-list.service/licenseList';
import { LicenseService } from '../../tn.licenses/license.service/license-service';
import { PopoverService } from '../../tn.popover/service.tn.popover';
import { BucketService } from '../../tn.pricing/buckets.service/buckets';
import { Pricing } from '../../tn.pricing/service.tn.pricing';
import { LocalProjects } from '../../tn.projects/local-projects.service/localProjects';
import { UserService } from '../../tn.user/service.tn.user';
import { CartService, cartToGA4Items } from '../service.tn.cart';

CartReview.$inject = [
    '$scope',
    '$state',
    'tn.cart.service',
    'tn.user.service',
    'tn.pricing.service',
    'orderService',
    '$tnPopover',
    'foundryService',
    'tnLicenseListService',
    'tnLicenseService',
    'tnBuckets',
    'localProjects',
    '$localStorage',
    'gtmService',
    'FontStyleService',
];

// these are all used, sorry!
export function CartReview(
    $scope: IScope,
    $state: StateService,
    cartService: CartService,
    userService: UserService,
    pricingService: Pricing, // eslint-disable-line max-params
    orderService: OrderService,
    $tnPopover: PopoverService,
    foundryService: FoundryService,
    licenseListService: LicenseListService,
    licenseService: LicenseService,
    bucketsService: BucketService,
    localProjects: LocalProjects,
    $localStorage: ngStorage.StorageService,
    gtm: GtmService,
    styleService: FontStyleService
): void {
    const vm = angular.extend(this, {
        totals: {},
        foundries: [],
        cart: cartService.getCart(),
        newLicenses: [],
        upgradedOrRenewalLicenses: [],
        licensesUpgradeDict: cartService.getLicensesUpgradeDict(),
        licenseCosts: {},
        taxRate: pricingService.getTaxRate(),
        acceptedEULA: false,
        purchaseWarning: false,
        isSubmitted: false,
        disableSubmission: false,
        submittingOrder: false,
        defaultFoundry: {},
        countLicenseTypes: cartService.countLicenseTypes,
        hasWebLicenseSelfHosted: cartService.hasWebLicenseSelfHosted(),
        isLoggedIn: userService.isLoggedIn(),
        savePopover: $tnPopover.popoverXS(
            angular.element('.popover-save-cart')
        ),
        emailCartPopover: $tnPopover.popoverXS(
            angular.element('.popover-cart-email')
        ),
        editPopover: $tnPopover.popoverXS(
            angular.element('.popover-styles-edit'),
            false
        ),
        upgradeRenewPopover: $tnPopover.popoverXS(
            angular.element('.popover-upgrade-renew-licenses'),
            false
        ),
        projects: localProjects.getSummaries(),
        buyStylesPopoverController: undefined, //: BuyStylesPopoverController
        stylesToFix: undefined, //: IFontStyle[]
    });

    const licenseQtyKeys = Object.keys(LICENSE_TYPE_DEFS).map(
        (right) => LICENSE_TYPE_DEFS[right].key
    );
    if ($state.current.name === 'home.checkout.review') {
        foundryService.getDjangoFoundries().then(() => {
            const foundryIdx = foundryService.djangoFoundriesIdxByDjanoId;
            // we're showing the cart review page send the analytics events
            gtm.view_cart({
                currency: 'USD',
                value: 0,
                items: cartToGA4Items(foundryIdx, vm.cart),
            });
        });
    }

    // Prevent checking out if we don't have a licensee
    if ($state.current.name === 'home.checkout.order') {
        if (vm.cart.license.length == 0) {
            $state.go('home.checkout.review');
        }
        if (!cartService.isLicenseeComplete()) {
            $state.go('^.billing');
        } else {
            // okay so, by the time we're here we definitely have a lincesee
            // the licensee, if country==='US', we absolutely have state and zip
            // if it's not, calculateTax bounces immediately so it's ~fine~
            pricingService.calculateTax(
                vm.cart.licensee.country,
                vm.cart.licensee.state,
                vm.cart.licensee.zipcode
            );
        }
    } else {
        // in the review phase, we just clear
        pricingService.clearTaxRate();
    }

    foundryService.getDjangoFoundries().then(() => {
        vm.foundries = foundryService.djangoFoundriesIdxByDjanoId;
    });

    recalculateTotals();
    registerCallbacks();

    vm.ensureAndGoToBilling = function () {
        $tnPopover.hide();
        const missing = cartService.getMissingLicenses(vm.cart);
        if (missing.length > 0) {
            styleService
                .list({
                    id: missing.map((license) => license.font.id),
                    page_size: missing.length + 10,
                })
                .then(({ results: styles }) => {
                    vm.stylesToFix = styles;
                    console.log('styles to fix', styles);
                    vm.buyStylesPopoverController.show();
                    vm.buyStylesPopoverController.goToState(
                        'correct-licensure'
                    );
                });
            console.warn('missing licenses', missing);
        } else {
            return vm.gotoBillingInfo();
        }
    };

    vm.gotoBillingInfo = function () {
        if (!vm.canGoToBilling()) {
            return;
        }
        $state.go('^.billing');
    };

    vm.gotoConfirmation = function (orderId) {
        return $state.go('^.confirmation', { orderId: orderId });
    };

    vm.splitCartLicenses = function () {
        cartService.splitCartLicenses(vm.cart.coupon.foundry);
        vm.splitCartLicensesVisible = false;
        vm.orderPlacementErrorVisible = false;
    };

    vm.placeOrder = function () {
        if (!vm.canPlaceOrder()) {
            vm.isSubmitted = true;
            return;
        }

        vm.cart.totals = vm.totals;
        vm.submittingOrder = true;
        vm.orderPlacementErrorVisible = false;
        vm.splitCartLicensesVisible = false;
        vm.orderPlacementError = '';

        orderService
            .placeOrder(vm.cart)
            .then((result) => {
                // queue up transition ...
                vm.gotoConfirmation(result.id).then(() => {
                    // state.go returns a Promise; clear cart when that's done!
                    cartService.emptyCart();
                    licenseService.refreshAll();
                });
                vm.disableSubmission = false;
            })
            .catch(function (err) {
                //eslint-disable-line complexity
                console.error('error occurred during submission', err);
                // sentry capture exception here

                vm.submittingOrder = false;

                if (err.status === 400) {
                    if (err.data) {
                        if (err.data.type == 'stripe') {
                            const stripeError = JSON.parse(err.data.error);
                            // everything in the POST viewset seems to return 400 if a CC error
                            // occured; send them back to the billing page in that case
                            const suffix =
                                stripeError.code == 'card_declined'
                                    ? ` code: ${stripeError.decline_code}`
                                    : '';
                            vm.orderPlacementError = `${stripeError.message}${suffix}`;
                            // $state.go('^.billing', { error: err.data });
                            return;
                        }
                        if (err.data.type === 'coupon') {
                            if (
                                err.data.status ===
                                'CouponOnlyForFoundryException'
                            ) {
                                vm.orderPlacementError = `The discount code entered is only valid when licensing
                                    families from ${vm.cart.coupon.foundry_name}. Families from other foundries
                                    must be licensed separately.`;
                                vm.splitCartLicensesVisible = true;
                            } else {
                                cartService.clearCoupon();
                                // catched expired and removed
                                vm.couponError = err.data.message;
                            }
                            return;
                        }
                        // this is the OrderSerializer returning errors
                        if (hasOwnProperty(err.data, 'errors')) {
                            if (
                                err.data.errors.errors.indexOf(
                                    'Upgrade or renewal is not allowed'
                                ) !== -1
                            ) {
                                const families =
                                    err.data.errors.families.join(', ');
                                vm.orderPlacementError =
                                    'Sorry we no longer license ' +
                                    families +
                                    ', please contact ' +
                                    'us for more information.';
                            } else if (
                                err.data.errors.errors.indexOf(
                                    'There are preview-only families in the order.'
                                ) !== -1
                            ) {
                                if (err.data.errors.families.length === 1) {
                                    vm.orderPlacementError =
                                        'The family ' +
                                        err.data.errors.families +
                                        ' is not currently for sale';
                                } else {
                                    let families = err.data.errors.families;
                                    families =
                                        families.slice(0, -1).join(',') +
                                        ' and ' +
                                        families.slice(-1);
                                    vm.orderPlacementError =
                                        'The families ' +
                                        families +
                                        ' are not currently for sale';
                                }
                            } else if (
                                err.data.errors.errors.indexOf(
                                    'There are preview-only styles in the order.'
                                ) !== -1
                            ) {
                                if (err.data.errors.styles.length === 1) {
                                    vm.orderPlacementError =
                                        'The style ' +
                                        err.data.errors.styles +
                                        ' is not currently for sale';
                                } else {
                                    let styles = err.data.errors.styles;
                                    styles =
                                        styles.slice(0, -1).join(',') +
                                        ' and ' +
                                        styles.slice(-1);
                                    vm.orderPlacementError =
                                        'The styles ' +
                                        styles +
                                        ' are not currently for sale';
                                }
                            } else {
                                // there is really only ever going to be on message and style here. This
                                // is because the OrderSerializer is and the OrderViewset.create are both
                                // misusing the drf ValidationError giving us a funkly array structure.
                                const messages = err.data.errors.errors || [];
                                const message =
                                    messages[0] ||
                                    'Unknown Order Validation Error';
                                const styles = err.data.errors.style || [];
                                const styleIdStr = styles[0] || undefined;
                                const styleId = parseInt(styleIdStr);
                                const styleName =
                                    styleId &&
                                    vm.cart.license.find((l) => {
                                        console.log(l.font.id, styleId);
                                        return l.font.id === styleId;
                                    })?.font.name;
                                if (message == 'web font right missing views') {
                                    vm.orderPlacementError =
                                        'Some fonts in your cart are missing license details, please `Edit Family` or `Edit All` to select your license terms.';
                                } else if (styleName) {
                                    vm.orderPlacementError = `${styleName} ${message}`;
                                } else {
                                    vm.orderPlacementError = message;
                                }
                            }
                        }
                    } else {
                        // this is a 400 from the Django viewset, no data
                        vm.orderPlacementError =
                            'Sorry, an error occurred during submission of this order. Please try again later.';
                    }
                } else {
                    // oh this is a 500 that's much worse, okay do what we can here
                    const tnEmail =
                        '<a href="mailto:info@typenetwork.com">info@typenetwork.com</a>';
                    vm.orderPlacementError =
                        'We&#8217;re sorry, but an error occurred during submission of this order. ';
                    if (
                        typeof err.data === 'object' &&
                        hasOwnProperty(err.data, 'detail') &&
                        err.data.detail === 'Do not resubmit'
                    ) {
                        vm.orderPlacementError +=
                            '<span class="semi-bold">Do not submit your order again, ' +
                            'you will be charged twice.</span> Please email support: ' +
                            tnEmail +
                            ' ' +
                            'and request we double check the license terms for this order.';
                        // PEBCAK protection
                        vm.disableSubmission = true;
                    } else {
                        vm.orderPlacementError +=
                            'Please try again later, and if you have any questions ' +
                            'please feel free to email: ' +
                            tnEmail +
                            '.';
                    }
                    if (err.data.message) {
                        vm.orderPlacementError += `<p></p><p>${err.data.message}</p>`;
                    }
                }
                vm.orderPlacementErrorVisible = true;
            });
    };

    vm.canGoToBilling = function () {
        if (
            vm.totals &&
            vm.totals.finals &&
            vm.totals.finals.withTax >= 1000000
        ) {
            // only allow invoicing of orders greater than a million
            if (userService.getUser()?.can_invoice) {
                return true;
            }
            return false;
        }
        return true;
    };

    vm.canPlaceOrder = function () {
        return (
            !vm.submittingOrder &&
            !vm.disableSubmission &&
            vm.cart.license.length > 0 &&
            (vm.eulaIsMissing ? vm.acceptedEULA : true) &&
            vm.cart.eula &&
            Object.keys(vm.cart.eula).every((foundryId) => {
                return vm.cart.eula[foundryId].accepted === true;
            })
        );
    };

    vm.removeFontRightsFromCart = function (licenses, upgradeOrRenew = false) {
        cartService.removeFontRightsFromCart(licenses, upgradeOrRenew);
    };

    vm.showSavePopover = function ($event) {
        const popoverController = angular
            .element('.popover-save-cart')
            .controller();
        popoverController.initialize();
        vm.savePopover.show($event.currentTarget);
    };

    vm.showEmailPopover = function ($event) {
        const popoverController = angular
            .element('.popover-cart-email')
            .controller();
        popoverController.showEmailForm();
        popoverController.currentState = 'email-new-cart';
        vm.emailCartPopover.show($event.currentTarget);
    };

    vm.showEditPopover = function ($event, licenses) {
        bucketsService
            .getIntersectingBuckets(licenses.map((l) => l.font))
            .then((intersectionLicenses) => {
                const updatedLicenses =
                    bucketsService.getClosestAcceptableLicenses(
                        intersectionLicenses,
                        licenses
                    );
                angular
                    .element('.popover-styles-edit')
                    .controller()
                    .initialize(vm.cart, updatedLicenses);
                vm.editPopover.show($event.currentTarget);
            });
    };

    vm.showUpgradeRenewPopover = function (
        $event,
        licenses,
        licenseType = null
    ) {
        if (vm.countLicenseTypes(licenses) === 1 || licenseType) {
            if (!licenseType) {
                const fontRights = licenses[0].fontRights;
                Object.keys(fontRights).forEach(function (fontType) {
                    if (
                        ['computers', 'ebooks', 'years'].some(
                            (key) => fontRights[fontType][key]
                        )
                    ) {
                        licenseType = fontType;
                    }
                });
            }
            angular
                .element('.popover-upgrade-renew-licenses')
                .controller()
                .initialize(vm.cart, licenses, [licenseType], 'cart');
            vm.upgradeRenewPopover.show($event.currentTarget);
        }
    };

    vm.eulaOrderBy = function (foundryId) {
        if (vm.foundries[foundryId]) {
            return vm.foundries[foundryId].name;
        }
    };

    function registerCallbacks() {
        // Update price totals when we change the cart or update licenses
        $scope.$watchCollection(
            '[vm.cart.coupon, vm.taxRate.rate]',
            recalculateTotals
        );
        $scope.$watch(
            'vm.cart.license',
            () => {
                checkUpgradedOrRenewalLicenses();
                separateUpgrades(vm.cart.license);
                recalculateTotals();
            },
            true
        );
        $scope.$watchCollection('vm.licensesUpgradeDict', recalculateTotals);

        userService.login$.subscribe(() => {
            vm.isLoggedIn = true;
            checkUpgradedOrRenewalLicenses();
        });

        userService.logout$.subscribe(() => {
            vm.isLoggedIn = false;
            // User just logged out, should not have purchace warnings
            vm.purchaseWarning = false;
            checkUpgradedOrRenewalLicenses();
        });

        $scope.$on('tn.fonts.buyStyles::continueCart', function () {
            vm.buyStylesPopoverController.hide();
            if (vm.cart.license.length > 0) {
                vm.gotoBillingInfo();
            }
        });
    }

    function checkUpgradedOrRenewalLicenses() {
        // There shouldn't be a currentSavedCart at this point, remove it in case
        // the user was previously seeing saved carts.
        $localStorage.currentSavedCart = null;
        if (vm.isLoggedIn) {
            licenseService.getLicensesFromServer().then((userOwnedLicenses) => {
                let purchaseWarning = false;
                vm.cart.license.forEach((license, i) => {
                    Object.keys(license.fontRights).forEach((fontType) => {
                        let exists = false;
                        userOwnedLicenses.forEach((userLicense) => {
                            //eslint-disable-line complexity
                            if (
                                (license.fontRights[fontType].renew ||
                                    license.fontRights[fontType].upgrade) &&
                                hasOwnProperty(
                                    userLicense.fontRights,
                                    fontType
                                ) &&
                                userLicense.fontRights[fontType].style ===
                                    license.font.id
                            ) {
                                exists = true;
                            }
                            // Check if this is an upgrade from hosted to self-hosted of a license that already
                            // belongs to a Project.
                            if (
                                hasOwnProperty(license.fontRights, 'web') &&
                                license.fontRights['web'].self_hosted ===
                                    true &&
                                hasOwnProperty(userLicense.fontRights, 'web') &&
                                userLicense.fontRights['web'].self_hosted ===
                                    false
                            ) {
                                if (_licenseInProject(license.font.id)) {
                                    purchaseWarning =
                                        'hostedToSelfhostedWarning';
                                }
                            }
                            // Check if the user might want to upgrade/renew instead of buying a new license
                            if (
                                _canUpgradeOrRenew(
                                    userLicense,
                                    license,
                                    fontType
                                )
                            ) {
                                if (purchaseWarning === false) {
                                    purchaseWarning = 'renewWarning';
                                }
                            }
                        });
                        if (
                            (license.fontRights[fontType].renew ||
                                license.fontRights[fontType].upgrade) &&
                            !exists
                        ) {
                            delete vm.cart.license[i].fontRights[fontType]
                                .renew;
                            delete vm.cart.license[i].fontRights[fontType]
                                .upgrade;
                        }
                    });
                    vm.purchaseWarning = purchaseWarning;
                });
            });
        } else {
            _unsetUpgradeRenewal();
        }
    }

    function _licenseInProject(styleId) {
        return vm.projects.some((project: IProjectSummary) =>
            project.styles.includes(styleId)
        );
    }

    function _unsetUpgradeRenewal() {
        // A user that is not logged in should see a renewal/upgrade cart as a new purchase.
        // Same should happen if the user has not previously purchased that style.
        vm.cart.license.forEach((license, i) => {
            Object.keys(license.fontRights).forEach((fontType) => {
                if (
                    license.fontRights[fontType].renew ||
                    license.fontRights[fontType].upgrade
                ) {
                    delete vm.cart.license[i].fontRights[fontType].renew;
                    delete vm.cart.license[i].fontRights[fontType].upgrade;
                }
            });
        });
        angular.copy({}, vm.licensesUpgradeDict);
    }

    function _canUpgradeOrRenew(userLicense, newLicense, fontType) {
        return (
            hasOwnProperty(vm.cart, fontType) &&
            userLicense.fontRights[fontType].style === newLicense.font.id &&
            !newLicense.fontRights[fontType].renew &&
            !newLicense.fontRights[fontType].upgrade &&
            licenseQtyKeys.some((key) => newLicense.fontRights[fontType][key])
        );
    }

    function recalculateTotals() {
        cartService.updateStyleDetails();
        //verify that vm.cart.eula exists
        if (!hasOwnProperty(vm.cart, 'eula')) {
            vm.cart.eula = {};
        }

        // Check if has self-hosted
        vm.hasWebLicenseSelfHosted = cartService.hasWebLicenseSelfHosted();

        pricingService
            .calculateTotals(
                vm.cart.license,
                vm.cart.coupon,
                vm.licensesUpgradeDict
            )
            .then((totals) => {
                vm.totals.oneTime = totals.oneTime;
                vm.totals.annual = totals.annual;
                vm.totals.threeYears = totals.threeYears;
                vm.totals.fiveYears = totals.fiveYears;
                vm.totals.subtotal = totals.subtotal;
                vm.totals.coupon = totals.coupon;
                vm.totals.finals = totals.finals;
            });

        pricingService
            .calculateLicensePrices(vm.cart.license)
            .then(function (costs) {
                costs.forEach((licensePrice) => {
                    const license = vm.cart.license.filter(
                        (li) => li.font.id === licensePrice.fontId
                    )[0];
                    vm.licenseCosts[licensePrice.fontId] = {};

                    vm.licenseCosts[licensePrice.fontId].upgrade = Object.keys(
                        license.fontRights
                    ).reduce((total, key) => {
                        if (
                            license.fontRights[key].upgrade ||
                            license.fontRights[key].renew
                        ) {
                            return total + licensePrice[key + 'Price'].base;
                        }
                        return total;
                    }, 0);

                    vm.licenseCosts[licensePrice.fontId].nonUpgrade =
                        Object.keys(license.fontRights).reduce((total, key) => {
                            if (
                                !license.fontRights[key].upgrade &&
                                !license.fontRights[key].renew
                            ) {
                                return total + licensePrice[key + 'Price'].base;
                            }
                            return total;
                        }, 0);
                });
            });

        //update foundry eulas
        //first ensure we have the foundries loaded
        foundryService.getDjangoFoundries().then(() => {
            /* cart.eula is a dict indexed by foundry of eula slugs, the licenses types and if the eula is accepted or not.

            ex. {'djr': {slug:'djr', types:['desktop', 'web'], accepted:false}};

            Fetch the list of foundries in the cart.
            Get cart license types per foundry
            Reduce them to the a unique list of slugs and combine with the license types.

            */

            const eulas = {};

            vm.cart.license.forEach(function (license) {
                license.font.foundry.forEach((foundryId) => {
                    const foundryObj =
                        foundryService.djangoFoundriesIdxByDjanoId[foundryId];
                    if (!eulas[foundryId]) {
                        //Copy accepted flag from cart, if it exists
                        const accepted =
                            vm.cart.eula[foundryId]?.accepted || false;

                        eulas[foundryId] = {
                            types: new Set(),
                            accepted: accepted,
                            eula: foundryObj.eula,
                        };
                    }
                    const licenseTypes = cartService.getLicenseTypes(license);
                    licenseTypes.forEach((license_type) => {
                        eulas[foundryId].types.add(license_type);
                    });
                });
            });

            // Now convert set back to an array
            Object.keys(eulas).forEach((key) => {
                eulas[key].types = Array.from(eulas[key].types);
            });

            vm.cart.eula = eulas;
        });
    }

    function separateUpgrades(licenses) {
        replaceCollection(
            vm.newLicenses,
            licenseListService.getNewLicenses(licenses)
        );
        replaceCollection(
            vm.upgradedOrRenewalLicenses,
            licenseListService.getUpgradeOrRenewalLicenses(licenses)
        );
    }
}
